import { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Flex, FormInstance, Input, InputNumber, ModalProps, Typography } from "antd";
import { useTranslation } from "react-i18next";
import SModalForm from "@/styles/SModalForm";
import { Customer, CustomerAdd } from "@/types/sales";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect } from "@/components/Selector";
import { getStorage } from "@/utils/storage";

interface CustomerFormProps extends ModalProps {
  handleCancel: () => void;
  handleOk: (data: CustomerAdd) => Promise<void>;
  data: Customer | null;
  leftTitle: ReactNode;
  confirmLoading: boolean;
}

const CustomerForm = (props: CustomerFormProps) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance<CustomerAdd>>(null);
  const [showExtFields, setShowExtFields] = useState<boolean>(false);

  const ordId = getStorage('curOrgId');

  useEffect(() => {
    if (!!!props?.data) {
      formRef?.current?.setFieldValue("orgId", ordId);
    }
  }, [props?.data, ordId])

  const getInput = (props: { maxLength: number; placeholder: string }) => {
    return (
      <Input
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        showCount
      />
    );
  };

  const getPercent = (props: { placeholder: string }) => {
    return (
      <InputNumber changeOnWheel
        max={100}
        min={0}
        style={{ width: "100%" }}
        placeholder={props.placeholder}
        formatter={(value) => `${value ? value + "%" : ""}`}
      />
    );
  };

  const getInputNumber = (props: { placeholder: string }) => {
    return (
      <InputNumber changeOnWheel
        precision={0}
        min={0}
        style={{ width: "100%" }}
        placeholder={props.placeholder}
      />
    );
  };

  return (
    <SModalForm<CustomerAdd>
      width={600}
      modelFormRef={formRef}
      formItems={[
        {
          label: t("sale.code"),
          name: "code",
          children: !props.data ? (
            <Input
              placeholder={t("sale.plCode")}
              maxLength={10}
              showCount
            />
          ) : (
            <Typography.Title level={4} style={{ margin: 0 }}>
              {props.data.code}
            </Typography.Title>
          ),
          required: true,
          rules: [{
            required: true, message: t("sale.plCode"), validator: (rule, value, callback) => {
              if (!value || (value && value.trim() === '')) {
                return Promise.reject();
              }
              return Promise.resolve();
            },
          }],
        },
        {
          label: t("sale.principal"),
          name: "principal",
          children: getInput({
            placeholder: t("sale.plPrincipal"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.name1"),
          name: "name1",
          children: getInput({
            placeholder: t("sale.plName1"),
            maxLength: 100,
          }),
        },
        {
          label: t("sale.name2"),
          name: "name2",
          children: getInput({
            placeholder: t("sale.plName2"),
            maxLength: 100,
          }),
        },
        {
          label: t("sale.shortName"),
          name: "shortName",
          children: getInput({
            placeholder: t("sale.plShortName"),
            maxLength: 20,
          }),
        },
        {
          label: t("sale.customerType"),
          name: "type",
          children: (
            <Dict key={"customerType"} type={DictEnum.SALE_CUSTOMER_TYPE} />
          ),
        },
        {
          label: t("sale.country"),
          name: "country",
          children: <CountrySelect />,
        },
        {
          label: t("sale.address"),
          name: "address",
          children:
            <Input.TextArea
              placeholder={t("sale.plAddress")}
              maxLength={500}
              showCount
              autoSize={{ maxRows: 4, minRows: 2 }}
            />
        },
        {
          label: t("sale.zipCode"),
          name: "zipCode",
          children: getInput({
            placeholder: t("sale.plZipCode"),
            maxLength: 10,
          }),
        },
        {
          label: t("sale.email"),
          name: "email",
          children: getInput({ placeholder: t("sale.plEmail"), maxLength: 100 }),
        },
        {
          label: t("sale.contact"),
          name: "contact",
          children: getInput({
            placeholder: t("sale.plContact"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.phone"),
          name: "phone",
          children: getInput({ placeholder: t("sale.plPhone"), maxLength: 50 }),
        },
        {
          label: t("sale.fax"),
          name: "fax",
          children: getInput({ placeholder: t("sale.plFax"), maxLength: 50 }),
        },
        {
          label: t("sale.mobile"),
          name: "mobile",
          children: getInput({
            placeholder: t("sale.plMobile"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.account1"),
          name: "account1",
          children: getInput({
            placeholder: t("sale.plAccount1"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.bank1"),
          name: "bank1",
          children: getInput({
            placeholder: t("sale.plBank1"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.swiftCode1"),
          name: "swiftCode1",
          children: getInput({
            placeholder: t("sale.plSwiftCode1"),
            maxLength: 20,
          }),
        },
        {
          label: t("sale.account2"),
          name: "account2",
          children: getInput({
            placeholder: t("sale.plAccount2"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.bank2"),
          name: "bank2",
          children: getInput({
            placeholder: t("sale.plBank2"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.swiftCode2"),
          name: "swiftCode2",
          children: getInput({
            placeholder: t("sale.plSwiftCode2"),
            maxLength: 20,
          }),
        },
        {
          label: t("sale.currency"),
          name: "currency",
          children: <CurrencySelect />,
        },
        {
          label: t("sale.saleType"),
          name: "saleType",
          children: (
            <Dict key={"saleType"} type={DictEnum.SALE_TYPE} />
          ),
        },
        {
          label: t("sale.creditLevel"),
          name: "creditLevel",
          children: <CreditLevelSelect />,
        },
        {
          label: t("sale.creditLine"),
          name: "creditLine",
          children: getInputNumber({ placeholder: t("sale.plCreditLine") }),
        },
        {
          label: t("sale.depositRate"),
          name: "depositRate",
          required: true,
          rules: [{
            required: true,
            message: t("sale.plDepositRate"),
            validator: (_rule, value) => {
              if (value === null || value === undefined) {
                return Promise.reject();
              }
              return Promise.resolve();
            },
          }],
          children: getPercent({ placeholder: t("sale.plDepositRate") }),
        },
        {
          label: t("sale.tradeTerm"),
          name: "tradeTerm",
          children: <Dict key={"tradeTerm"} type={DictEnum.SALE_TRADE_TERM} />,
        },
        {
          label: t("sale.payMethod"),
          name: "payMethod",
          children: <Dict key={"payMethod"} type={DictEnum.SALE_PAY_METHOD} />,
        },
        {
          label: t("sale.commissionRate"),
          name: "commissionRate",
          children: getPercent({ placeholder: t("sale.plDictCommissionRate") }),
        },
        {
          label: t("sale.org"),
          name: "orgId",
          children: <DepartmentSelect placeholder={t("sale.org")} allowClear={false} />,
        },
        {
          label: t("sale.billingDays"),
          name: "billingDays",
          children: getInputNumber({ placeholder: t("sale.plBillingDays") }),
        },
        {
          label: t("sale.affiliates"),
          name: "affiliates",
          children: getInput({
            placeholder: t("sale.plAffiliates"),
            maxLength: 50,
          }),
        },
        {
          label: t("sale.taxRate"),
          name: "taxRate",
          children: getPercent({ placeholder: t("sale.plTaxRate") }),
        },
        {
          label: t("sale.licenseId"),
          name: "licenseId",
          children: getInput({ placeholder: t("sale.licenseId"), maxLength: 100 }),
        },
        {
          label: t("sale.licenseAddress"),
          name: "licenseAddress",
          children: getInput({ placeholder: t("sale.licenseAddress"), maxLength: 500 }),
        },
        {
          label: t("sale.invoiceAddress"),
          name: "invoiceAddress",
          children: getInput({ placeholder: t("sale.invoiceAddress"), maxLength: 500 }),
        },
        {
          noStyle: true,
          children: (
            <Flex justify="end" style={{ marginTop: -18 }}>
              <Button type="link" onClick={() => setShowExtFields(!showExtFields)}>
                {showExtFields ? t('common.collapse') : t('common.expand')}
              </Button>
            </Flex>
          )
        },
        {
          label: "wmsApi",
          name: "wmsApi",
          hidden: !showExtFields,
          children: getInput({
            placeholder: "wmsApi",
            maxLength: 200,
          }),
        },
      ]}
      {...props}
    />
  );
};

export default CustomerForm;
